import React, { useState, useEffect } from 'react';
import {
    Card,
    Tag,
    Descriptions,
    Divider,
    Table,
    Button,
    Space,
    Timeline,
    Form,
    InputNumber,
    message,
    Spin,
    Modal,
    Input,
    Popconfirm,
    Select,
    Grid,
    Row,
    Col
} from 'antd';
import moment from 'moment';
import axiosInstance from '../../../axiosInstance';
import { shopId, STATUS_COLORS, TRANSFER_ACTIONS, TRANSFER_STATUS } from '../../constants';
import { useParams } from 'react-router-dom';
import { printTransfer } from './report/transferPdfExport';
import { ExclamationCircleFilled } from '@ant-design/icons';

const { Option } = Select;
const { useBreakpoint } = Grid;

const TransferWorkflow = ({ onStatusChange }) => {
    const [transfer, setTransfer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [receiveForm] = Form.useForm();
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancelNote, setCancelNote] = useState('');
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const { id } = useParams();
    const screens = useBreakpoint();
    const [printLoading, setPrintLoading] = useState(false);
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorDetails, setErrorDetails] = useState('');

    useEffect(() => {
        fetchTransfer();
    }, [id]);

    const fetchTransfer = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/transfers/${id}`);
            setTransfer(response.data.transfer);

            if (response.data.transfer.status === TRANSFER_STATUS.SHIPPED) {
                const initialValues = {
                    items: {}
                };

                response.data.transfer.items.forEach(item => {
                    initialValues.items[item.id] = {
                        quantity_received: item.quantity_shipped
                    };
                });

                receiveForm.setFieldsValue(initialValues);
            }
        } catch (error) {
            message.error('Failed to load transfer details');
        } finally {
            setLoading(false);
        }
    };



    const handlePrint = async () => {
        if (!transfer) {
            message.error('No transfer data available');
            return;
        }

        setPrintLoading(true);
        try {
            await printTransfer(transfer);
            message.success('Transfer document generated successfully!');
        } catch (error) {
            console.error('Print error:', error);
            setErrorDetails(error.message || 'Unknown error occurred');
            setErrorModalVisible(true);
            message.error('Failed to generate PDF');
        } finally {
            setPrintLoading(false);
        }
    };

    const fetchProducts = async () => {
        setProductsLoading(true);
        try {
            const response = await axiosInstance.get(`/products?shopId=${transfer.source_shop_id}`);
            setProducts(response.data.products);
        } catch (error) {
            message.error('Failed to load products');
        } finally {
            setProductsLoading(false);
        }
    };

    const handleAction = async (action) => {
        setActionLoading(true);
        try {
            let response;

            switch (action) {
                case TRANSFER_ACTIONS.SUBMIT:
                    response = await axiosInstance.post(`/transfers/${id}/submit`);
                    break;
                case TRANSFER_ACTIONS.APPROVE:
                    response = await axiosInstance.post(`/transfers/${id}/approve`);
                    break;
                case TRANSFER_ACTIONS.SHIP:
                    response = await axiosInstance.post(`/transfers/${id}/ship`);
                    break;
                case TRANSFER_ACTIONS.RECEIVE:
                    const values = await receiveForm.validateFields();
                    const itemsArray = transfer.items.map(item => ({
                        id: item.id,
                        quantity_received: values.items[item.id].quantity_received
                    }));
                    response = await axiosInstance.post(`/transfers/${id}/receive`, {
                        items: itemsArray
                    });
                    break;
                default:
                    throw new Error('Invalid action');
            }

            message.success(response.data.message);
            fetchTransfer();
            if (onStatusChange) {
                onStatusChange(response.data.transfer.status);
            }
        } catch (error) {
            console.error('Action error:', error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                message.error(errorMessages.join(', '));
            } else {
                message.error(error.response?.data?.message || error.message || 'Action failed');
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            await axiosInstance.post(`/transfers/${id}/cancel`, { notes: cancelNote });
            message.success('Transfer cancelled successfully');
            setCancelModalVisible(false);
            fetchTransfer();
            if (onStatusChange) {
                onStatusChange(TRANSFER_STATUS.CANCELLED);
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to cancel transfer');
        }
    };

    const handleAddItem = () => {
        setCurrentItem(null);
        fetchProducts();
        setEditModalVisible(true);
    };

    const handleEditItem = (item) => {
        setCurrentItem(item);
        fetchProducts();
        setEditModalVisible(true);
    };

    const handleRemoveItem = async (itemId) => {
        try {
            await axiosInstance.delete(`/transfers/${id}/items/${itemId}`);
            message.success('Item removed successfully');
            fetchTransfer();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to remove item');
        }
    };

    const handleSaveItem = async (values) => {
        try {
            if (currentItem) {
                // Update existing item
                await axiosInstance.put(`/transfers/${id}/items/${currentItem.id}`, values);
                message.success('Item updated successfully');
            } else {
                // Add new item
                await axiosInstance.post(`/transfers/${id}/items`, values);
                message.success('Item added successfully');
            }
            setEditModalVisible(false);
            fetchTransfer();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to save item');
        }
    };

    if (loading) return <Spin size="large" />;

    const canCancel = [TRANSFER_STATUS.DRAFT, TRANSFER_STATUS.PENDING_APPROVAL].includes(transfer.status);
    const canEditItems = [TRANSFER_STATUS.DRAFT].includes(transfer.status);

    const itemColumns = [
        {
            title: 'Product',
            dataIndex: ['product', 'name'],
            responsive: ['md']
        },
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            responsive: ['md']
        },
        {
            title: 'Requested',
            dataIndex: 'quantity_requested',
            responsive: ['sm']
        },
        transfer.status === TRANSFER_STATUS.SHIPPED && {
            title: 'Shipped',
            dataIndex: 'quantity_shipped',
            responsive: ['sm']
        },
        transfer.status === TRANSFER_STATUS.RECEIVED && {
            title: 'Received',
            dataIndex: 'quantity_received',
            responsive: ['sm']
        },
        transfer.status === TRANSFER_STATUS.SHIPPED && {
            title: 'Received',
            render: (_, record) => (
                <Form.Item
                    name={['items', record.id, 'quantity_received']}
                    initialValue={record.quantity_shipped}
                    rules={[
                        { required: true, message: 'Required' },
                        {
                            validator: (_, value) => {
                                if (value <= record.quantity_shipped) {
                                    return Promise.resolve();
                                }
                                return Promise.reject('Cannot receive more than shipped');
                            }
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        max={record.quantity_shipped}
                        disabled={!(Number(shopId) === Number(transfer.target_shop.id))}
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            )
        },
        canEditItems && {
            title: 'Actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" onClick={() => handleEditItem(record)} size="small">
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to remove this item?"
                        onConfirm={() => handleRemoveItem(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger size="small">
                            Remove
                        </Button>
                    </Popconfirm>
                </Space>
            ),
            fixed: screens.md ? false : 'right'
        }
    ].filter(Boolean);

    return (
        <>
            <Card
                title={`Transfer #${transfer.transfer_number}`}
                extra={
                    <Space>
                        <Tag color={STATUS_COLORS[transfer.status]}>{transfer.status}</Tag>
                        <Button
                            onClick={handlePrint}
                            loading={printLoading}
                        >
                            Print Transfer
                        </Button>
                    </Space>
                }
            >
                <Descriptions
                    bordered
                    column={{ xs: 1, sm: 1, md: 2 }}
                    size={screens.md ? 'default' : 'small'}
                >
                    <Descriptions.Item label="From">
                        <span style={{ textTransform: 'uppercase' }}>
                            {transfer.source_shop.name} {transfer.source_shop.type}
                        </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="To">
                        <span style={{ textTransform: 'uppercase' }}>
                            {transfer.target_shop.name} {transfer.source_shop.type}
                        </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created By">{transfer.creator.name}</Descriptions.Item>
                    <Descriptions.Item label="Created At">
                        {moment(transfer.created_at).format('LLL')}
                    </Descriptions.Item>

                    {transfer.approved_by && (
                        <>
                            <Descriptions.Item label="Approved By">{transfer.approver.name}</Descriptions.Item>
                            <Descriptions.Item label="Approved At">
                                {moment(transfer.approved_at).format('LLL')}
                            </Descriptions.Item>
                        </>
                    )}

                    {transfer.shipped_at && (
                        <>
                            <Descriptions.Item label="Shipped At">
                                {moment(transfer.shipped_at).format('LLL')}
                            </Descriptions.Item>
                        </>
                    )}

                    {transfer.notes && (
                        <Descriptions.Item label="Notes" span={2}>
                            {transfer.notes}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <Divider orientation="left">Items</Divider>
                <Form form={receiveForm}>
                    <Table
                        columns={itemColumns}
                        dataSource={transfer.items}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        style={{ maxWidth: '100%' }}
                        size={screens.md ? 'default' : 'small'}
                        footer={canEditItems ? () => (
                            <Button type="primary" onClick={handleAddItem}>
                                Add Item
                            </Button>
                        ) : null}
                    />
                </Form>

                <Divider orientation="left">Actions</Divider>
                <Space wrap>
                    {transfer.status === TRANSFER_STATUS.DRAFT && (
                        <Button
                            type="primary"
                            onClick={() => handleAction(TRANSFER_ACTIONS.SUBMIT)}
                            loading={actionLoading}
                            size={screens.md ? 'default' : 'small'}
                        >
                            Submit for Approval
                        </Button>
                    )}

                    {transfer.status === TRANSFER_STATUS.PENDING_APPROVAL && (
                        <Button
                            type="primary"
                            onClick={() => handleAction(TRANSFER_ACTIONS.APPROVE)}
                            loading={actionLoading}
                            size={screens.md ? 'default' : 'small'}
                        >
                            Approve Transfer
                        </Button>
                    )}

                    {transfer.status === TRANSFER_STATUS.APPROVED && (
                        <Button
                            type="primary"
                            onClick={() => handleAction(TRANSFER_ACTIONS.SHIP)}
                            loading={actionLoading}
                            size={screens.md ? 'default' : 'small'}
                        >
                            Ship Products
                        </Button>
                    )}

                    {transfer.status === TRANSFER_STATUS.SHIPPED && Number(shopId) === Number(transfer.target_shop.id) && (
                        <Button
                            type="primary"
                            onClick={() => handleAction(TRANSFER_ACTIONS.RECEIVE)}
                            loading={actionLoading}
                            size={screens.md ? 'default' : 'small'}
                        >
                            Receive Products
                        </Button>
                    )}

                    {canCancel && (
                        <Button
                            danger
                            onClick={() => setCancelModalVisible(true)}
                            loading={actionLoading}
                            size={screens.md ? 'default' : 'small'}
                        >
                            Cancel Transfer
                        </Button>
                    )}
                </Space>

                <Divider orientation="left">Audit Log</Divider>
                <Timeline mode={screens.md ? 'left' : 'alternate'}>
                    {(transfer.logs || []).map(log => (
                        <Timeline.Item key={log.id}>
                            <p>
                                <strong>{log.action.toUpperCase()}</strong> by {log.performer.name}
                            </p>
                            <p>{moment(log.created_at).format('LLL')}</p>
                            {log.notes && <p>Note: {log.notes}</p>}
                        </Timeline.Item>
                    ))}
                </Timeline>

                <Modal
                    title="Cancel Transfer"
                    visible={cancelModalVisible}
                    onOk={handleCancel}
                    onCancel={() => setCancelModalVisible(false)}
                    okText="Confirm Cancel"
                    okButtonProps={{ danger: true }}
                    width={screens.md ? '50%' : '90%'}
                >
                    <p>Are you sure you want to cancel this transfer?</p>
                    <Input.TextArea
                        placeholder="Reason for cancellation (optional)"
                        value={cancelNote}
                        onChange={(e) => setCancelNote(e.target.value)}
                        rows={4}
                    />
                </Modal>

                <Modal
                    title={currentItem ? 'Edit Item' : 'Add Item'}
                    visible={editModalVisible}
                    onCancel={() => setEditModalVisible(false)}
                    footer={null}
                    width={screens.md ? '50%' : '90%'}
                >
                    <Form
                        initialValues={currentItem || { quantity_requested: 1 }}
                        onFinish={handleSaveItem}
                        layout="vertical"
                    >
                        <Form.Item
                            name="product_id"
                            label="Product"
                            rules={[{ required: true, message: 'Please select a product' }]}
                        >
                            <Select
                                loading={productsLoading}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    const children = Array.isArray(option.children)
                                        ? option.children.join(' ')
                                        : option.children.toString();
                                    return children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                }}
                                disabled={!!currentItem}
                            >
                                {products.map(product => (
                                    <Option key={product.id} value={product.id}>
                                        {product.name} ({product.barcode})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="quantity_requested"
                            label="Quantity"
                            rules={[
                                { required: true, message: 'Please enter quantity' },
                                { type: 'number', min: 1, message: 'Quantity must be at least 1' }
                            ]}
                        >
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Save
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </Card>
            <Modal
                title={
                    <span style={{ color: '#ff4d4f' }}>
                        <ExclamationCircleFilled style={{ marginRight: 8 }} />
                        PDF Generation Error
                    </span>
                }
                visible={errorModalVisible}
                onOk={() => setErrorModalVisible(false)}
                onCancel={() => setErrorModalVisible(false)}
                okText="OK"
                cancelText="Close"
            >
                <p>Failed to generate PDF document:</p>
                <p style={{ fontWeight: 'bold' }}>{errorDetails}</p>
                <p>Please check the console for more details and try again.</p>
            </Modal>
        </>
    );
};

export default TransferWorkflow;